import type {CSSProperties} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import {useLabelledControl} from './LabelContext';
import {InputWrapper} from './TextInputWrapper';

// The text metrics `TextAreaInput` and `AutoGrowMirror` share, so the mirror
// wraps the value where the field does.
const TEXT_METRICS = {
  boxSizing: 'border-box',
  fontFamily: DEFAULT_FONT,
  fontSize: 14,
  lineHeight: '20px',
  padding: '8px 10px',
} as const;

const TextAreaInput = styled(
  'textarea',
  (
    {
      $autoGrow,
      $resize = 'vertical',
      rows,
    }: {
      $autoGrow?: boolean;
      $resize?: 'both' | 'horizontal' | 'vertical' | 'none';
      rows?: number;
    },
    designColors,
  ) => ({
    appearance: 'none',
    backgroundColor: 'transparent',
    border: 0,
    borderRadius: 4,
    ...TEXT_METRICS,
    // The auto-grow measurement sets the height to scrollHeight, which
    // includes padding; border-box keeps that from compounding.
    boxSizing: $autoGrow ? 'border-box' : undefined,
    color: designColors.contentText,
    height: '100%',
    minHeight: rows ? undefined : 120,
    overflowY: $autoGrow ? 'hidden' : undefined,
    resize: $autoGrow ? 'none' : $resize,
    width: '100%',

    // The `InputWrapper` around it draws the focus ring.
    ':focus': {
      // eslint-disable-next-line no-restricted-syntax -- the wrapper draws the ring
      outline: 'none',
    },

    '::placeholder': {color: designColors.contentTextMuted},
    '::-webkit-placeholder': {color: designColors.contentTextMuted},
  }),
  {className: 'TextArea'},
);

// An invisible copy of the value, laid out like the field's text. Its size
// changes whenever the text would wrap to a different height: a new value, a
// narrower field, a web font that loads late, or user styles for text spacing
// (WCAG 1.4.12). Nothing tells the textarea about those last ones, and it
// hides its overflow, so a `ResizeObserver` on the mirror re-measures it.
const AutoGrowMirror = styled('div', {
  ...TEXT_METRICS,
  left: 0,
  overflowWrap: 'break-word',
  pointerEvents: 'none',
  position: 'absolute',
  top: 0,
  visibility: 'hidden',
  whiteSpace: 'pre-wrap',
  width: '100%',
});

type Props = {
  'aria-describedby'?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  autoGrow?: boolean;
  disabled?: boolean;
  id?: string;
  /** Marks the field invalid. A surrounding `<Label>` with an error does too. */
  invalid?: boolean;
  /** The language of the text, for a page in another language (WCAG 3.1.2). */
  lang?: string;
  maxLength?: number;
  minLength?: number;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLTextAreaElement>['onBlur'];
    onChange: React.TextareaHTMLAttributes<HTMLTextAreaElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLTextAreaElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLTextAreaElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLTextAreaElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLTextAreaElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLTextAreaElement>['onKeyPress'];
  }>;
  onChange?: (value: string) => void;
  onRef?: React.Ref<HTMLTextAreaElement | null>;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  resize?: 'both' | 'horizontal' | 'vertical' | 'none';
  rows?: number;
  style?: CSSProperties;
  tabIndex?: number;
  value: string;
};

const TextArea = (
  {
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    autoGrow,
    disabled,
    id,
    invalid,
    nativeEvents,
    onChange,
    resize,
    style,
    value,
    ...rest
  }: Props,
  ref: React.Ref<HTMLTextAreaElement>,
) => {
  const labelling = useLabelledControl({
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    id,
    invalid,
  });
  const ownRef = React.useRef<HTMLTextAreaElement | null>(null);
  const handleRef = React.useCallback(
    (el: HTMLTextAreaElement | null) => {
      ownRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current =
          el;
      }
    },
    [ref],
  );

  const resizeToFit = React.useCallback(() => {
    const el = ownRef.current;
    if (!el) {
      return;
    }
    // Collapse first so the textarea can shrink when content is removed.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useLayoutEffect(() => {
    if (autoGrow) {
      resizeToFit();
    }
  }, [autoGrow, resizeToFit, value]);

  const mirrorRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!autoGrow) {
      return;
    }
    const mirror = mirrorRef.current;
    if (mirror && typeof ResizeObserver !== 'undefined') {
      // The mirror is out of flow, so setting the textarea's height cannot
      // resize it again, and there is no observer loop.
      const observer = new ResizeObserver(resizeToFit);
      observer.observe(mirror);
      return () => {
        observer.disconnect();
      };
    }
    // Width changes re-wrap the text, which changes the required height.
    window.addEventListener('resize', resizeToFit);
    return () => {
      window.removeEventListener('resize', resizeToFit);
    };
  }, [autoGrow, resizeToFit]);

  const nativeOnChange = nativeEvents?.onChange;
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement, HTMLTextAreaElement>) => {
      e.stopPropagation();
      e.preventDefault();

      onChange?.(e.target.value);
      nativeOnChange?.(e);
    },
    [onChange, nativeOnChange],
  );
  return (
    <InputWrapper
      $disabled={!!disabled}
      $invalid={Boolean(labelling['aria-invalid'])}
      style={{
        height: 'auto',
        position: autoGrow ? 'relative' : undefined,
        ...style,
      }}
    >
      {autoGrow && (
        <AutoGrowMirror aria-hidden dir="auto" ref={mirrorRef}>
          {value}
        </AutoGrowMirror>
      )}
      <TextAreaInput
        $autoGrow={autoGrow}
        $resize={resize}
        disabled={disabled}
        {...rest}
        {...labelling}
        dir="auto"
        {...nativeEvents}
        onChange={handleChange}
        ref={handleRef}
        value={value}
      />
    </InputWrapper>
  );
};

export default React.forwardRef<HTMLTextAreaElement, Props>(TextArea);
